import { PhoneNumberUtil } from 'google-libphonenumber';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';
import useParkingService from '../../../hooks/parking-services/useParkingService.ts';
import useUpdateParkingServiceMetadata from '../../../hooks/parking-services/useUpdateParkingServiceMetadata.ts';
import useUpdateParkingServiceOperationHours from '../../../hooks/parking-services/useUpdateParkingServiceOperationHours.ts';
import useEnableParkingService from '../../../hooks/parking-services/useEnableParkingService.ts';
import usePauseParkingService from '../../../hooks/parking-services/usePauseParkingService.ts';
import useDisableParkingService from '../../../hooks/parking-services/useDisableParkingService.ts';
import {
  UpdateParkingServiceMetadataRequest,
  UpdateParkingServiceOperationHoursRequest,
  UpdateParkingServicePaymentMethodsRequest,
  UpdateParkingServiceTagsRequest,
} from '../../../api/requests/parking-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { notifySuccess } from '../../../utils/NotifySuccess.tsx';
import { LoadingOverlay, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import MutateUpdateParkingServiceForm, {
  MutateUpdateParkingServiceFormValues,
} from '../../ui/places/MutateUpdateParkingServiceForm.tsx';
import useUpdateParkingServiceTags from '../../../hooks/parking-services/useUpdateParkingServiceTags.ts';
import useUpdateParkingServicePaymentMethods from '../../../hooks/parking-services/useUpdateParkingServicePaymentMethods.ts';
import useUpdateParkingServiceStoreIds from '../../../hooks/parking-services/useUpdateParkingServiceStoreIds.ts';
import {
  buildStoreUrl,
  resolveStoreIdsChange,
} from '../../../utils/storeUrl.ts';

const phoneUtil = PhoneNumberUtil.getInstance();

interface UpdateParkingServiceMetadataDrawerProps {
  placeId: string;
  parkingServiceId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const UpdateParkingServiceMetadataDrawer = ({
  placeId,
  parkingServiceId,
  onSuccess,
  onCancel,
}: UpdateParkingServiceMetadataDrawerProps) => {
  const queryClient = useQueryClient();

  const { data: parkingService, isLoading: isParkingServiceLoading } =
    useParkingService(parkingServiceId);

  const { mutateAsync: updateParkingServiceMetadata } =
    useUpdateParkingServiceMetadata();
  const { mutateAsync: updateParkingServiceOperationHours } =
    useUpdateParkingServiceOperationHours();
  const { mutateAsync: updateParkingServiceStoreIds } =
    useUpdateParkingServiceStoreIds();
  const { mutateAsync: updateParkingServiceTags } =
    useUpdateParkingServiceTags();
  const { mutateAsync: updateParkingServicePaymentMethods } =
    useUpdateParkingServicePaymentMethods();
  const { mutateAsync: enableParkingService } = useEnableParkingService();
  const { mutateAsync: pauseParkingService } = usePauseParkingService();
  const { mutateAsync: disableParkingService } = useDisableParkingService();

  const { mutate: handleSubmitAll, isPending: isUpdateParkingServicePending } =
    useMutation<void, ErrorResponse, MutateUpdateParkingServiceFormValues>({
      mutationFn: async (formValues) => {
        const storeIdsToSubmit = resolveStoreIdsChange(
          formValues.storeIdUrl,
          'PARKING',
          parkingService.storeIds,
        );
        if (storeIdsToSubmit !== null) {
          await updateParkingServiceStoreIds({
            serviceId: parkingService.id,
            storeIds: storeIdsToSubmit,
          });
        }

        const parsedPhoneNumber =
          formValues.phoneNumberCountryCode !== '' &&
          formValues.phoneNumberValue !== ''
            ? phoneUtil.parse(
                `${formValues.phoneNumberCountryCode}${formValues.phoneNumberValue}`,
              )
            : null;

        const updateParkingServiceMetadataRequest: UpdateParkingServiceMetadataRequest =
          {
            serviceId: parkingService.id,
            name: formValues.name.trim(),
            brandId: formValues.brandId ?? '',
            phoneNumber: {
              countryCode: formValues.phoneNumberCountryCode ?? '',
              number: parsedPhoneNumber
                ? phoneUtil.getNationalSignificantNumber(parsedPhoneNumber)
                : '',
            },
            website: formValues.website,
            notes: formValues.notes,
            pricingSource: parkingService.pricingSource,
            heightLimit: parkingService.heightLimit,
            hasRestroom: parkingService.hasRestroom,
            hasElevator: parkingService.hasElevator,
            hasParkingBooth: parkingService.hasParkingBooth,
            buildingTypes: parkingService.buildingTypes,
            mechanicalSpaces: parkingService.mechanicalSpaces,
            parkingLotTypes: parkingService.parkingLotTypes,
            floorLevels: parkingService.floorLevels,
          };

        await updateParkingServiceMetadata(updateParkingServiceMetadataRequest);

        if (formValues.serviceStatus === 'ONLINE') {
          await enableParkingService(parkingService.id);
        } else if (formValues.serviceStatus === 'TEMPORARY_OFFLINE') {
          await pauseParkingService(parkingService.id);
        } else {
          await disableParkingService(parkingService.id);
        }

        const updateParkingServiceTagsRequest: UpdateParkingServiceTagsRequest =
          {
            serviceId: parkingService.id,
            tags: formValues.tags,
          };

        await updateParkingServiceTags(updateParkingServiceTagsRequest);

        const updateParkingServicePaymentMethodsRequest: UpdateParkingServicePaymentMethodsRequest =
          {
            serviceId: parkingService.id,
            paymentMethods: formValues.paymentMethods,
          };

        await updateParkingServicePaymentMethods(
          updateParkingServicePaymentMethodsRequest,
        );

        // 一律送出表單當下顯示的營業時間，確保 drawer 上看到的資料跟實際寫入的完全一致
        const updateParkingServiceOperationHoursRequest: UpdateParkingServiceOperationHoursRequest =
          {
            serviceId: parkingService.id,
            operationHours: formValues.operationHours,
          };

        await updateParkingServiceOperationHours(
          updateParkingServiceOperationHoursRequest,
        );
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['parkingServices', parkingService.id],
          exact: true,
        });
        await queryClient.invalidateQueries({
          queryKey: ['places', placeId],
          exact: true,
        });
        notifySuccess('更新停車服務成功');
        onSuccess();
      },
      onError: (error) => {
        notifyError('更新停車服務失敗', error.message);
      },
    });

  const handleFormSubmit = (
    formValues: MutateUpdateParkingServiceFormValues,
  ) => {
    let storeIdsToSubmit: ReturnType<typeof resolveStoreIdsChange>;
    try {
      storeIdsToSubmit = resolveStoreIdsChange(
        formValues.storeIdUrl,
        'PARKING',
        parkingService.storeIds,
      );
    } catch {
      return;
    }

    if (storeIdsToSubmit === null) {
      handleSubmitAll(formValues);
      return;
    }

    const isUnbinding = storeIdsToSubmit.length === 0;
    modals.openConfirmModal({
      title: (
        <Text style={{ fontSize: 16, fontWeight: 700 }}>
          {isUnbinding ? '解除綁定商店' : '綁定商店'}
        </Text>
      ),
      children: (
        <Text size="sm">
          {isUnbinding
            ? '解除綁定後，目前的營業時間與停車費率不會被清空，會維持最後一次同步的資料，確定要繼續嗎？'
            : '綁定後將會用 autoplus 現有的營業時間與停車費率覆蓋這個服務目前的資料，確定要繼續嗎？'}
        </Text>
      ),
      labels: { confirm: '是，我確定', cancel: '取消' },
      confirmProps: isUnbinding ? { color: 'red' } : undefined,
      onConfirm: () => handleSubmitAll(formValues),
    });
  };

  const handleClose = () => {
    onCancel();
  };

  if (isParkingServiceLoading) return <LoadingOverlay />;

  return (
    <MutateUpdateParkingServiceForm
      initialValues={{
        serviceId: parkingService.id,
        name: parkingService.name,
        serviceStatus: parkingService.serviceStatus,
        brandId: parkingService.brandId,
        storeIdUrl: parkingService.storeIds[0]
          ? buildStoreUrl(parkingService.storeIds[0])
          : '',
        operationHours: parkingService.operationHours,
        phoneNumberCountryCode: parkingService.phoneNumber?.countryCode,
        phoneNumberValue: parkingService.phoneNumber?.number,
        website: parkingService.website,
        notes: parkingService.notes,
        tags: parkingService.tags,
        paymentMethods: parkingService.paymentMethods,
      }}
      onSubmit={handleFormSubmit}
      onCancel={handleClose}
      isSubmitting={isUpdateParkingServicePending}
      isUpdating={true}
    />
  );
};

export { UpdateParkingServiceMetadataDrawer };
